import { VariableType } from '@/modules/global/stores/VariableTypeStore'

/**
 * Normalizes a variable type string from the API to a consistent lowercase format.
 * Handles various API formats like 'date_time', 'date-time', 'Date', 'DATE', etc.
 * @param value - The variable type string from the API (can be undefined)
 * @returns Normalized lowercase string without spaces, dashes, or underscores
 */
export function normalizeVariableType(value?: string): string {
  return (value || '').toLowerCase().replace(/[\s-_]/g, '')
}

/**
 * Maps a UI VariableType to the API format (lowercase string).
 * @param type - The VariableType from the UI
 * @returns The API format string (lowercase)
 */
export function mapTypeToApi(type: VariableType): string {
  if (type === 'Number') return 'number'
  if (type === 'E-mail') return 'email'
  if (type === 'Date') return 'date'
  if (type === 'DateTime') return 'datetime'
  if (type === 'Phone') return 'phone'
  if (type === 'Dropdown') return 'dropdown'
  return 'text'
}

/**
 * Maps an API variable type string to the UI VariableType format.
 * Handles various API formats and converts them to the standard UI format.
 * @param apiType - The variable type string from the API
 * @returns The UI VariableType format
 */
export function mapApiTypeToUI(apiType: string): VariableType {
  const normalized = normalizeVariableType(apiType)
  if (normalized === 'number') return 'Number'
  if (normalized === 'email') return 'E-mail'
  if (normalized === 'date') return 'Date'
  if (normalized === 'datetime') return 'DateTime'
  if (normalized === 'phone') return 'Phone'
  if (normalized === 'dropdown') return 'Dropdown'
  return 'Text'
}

/**
 * Checks if a variable type is a date-like type (Date or DateTime).
 * @param variableType - The variable type string (can be in any format)
 * @returns True if the type is Date or DateTime
 */
export function isDateLikeType(variableType: string): boolean {
  const normalized = normalizeVariableType(variableType)
  return normalized === 'date' || normalized === 'datetime'
}

/**
 * Checks if a variable type is a dropdown type.
 * @param variableType - The variable type string (can be in any format)
 * @returns True if the type is Dropdown
 */
export function isDropdownType(variableType: string): boolean {
  const normalized = normalizeVariableType(variableType)
  return normalized === 'dropdown'
}
